import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  CheckboxGrid,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnMissedVisitForm,
  updateRnMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CalendarIcon,
  DeleteIcon,
  HeartPulseIcon,
  HelpingIcon,
  NurseIcon,
  PhoneIcon,
  PlusIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTEMPT_METHOD_OPTIONS,
  DISCIPLINE_OPTIONS,
  INTERVENTION_OPTIONS,
  MISSED_REASON_OPTIONS,
  NOTIFY_METHOD_OPTIONS,
  RISK_LEVEL_OPTIONS,
  YES_NO_OPTIONS,
  emptyAttemptLog,
  emptyRnMissedVisitPayload,
  toggleInList,
  today,
} from "./rnMissedVisitContent";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";

const MISSED_VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

/** Every payload field that holds a checkbox-group selection. */
const ARRAY_KEYS = [
  "missedReasons",
  "safetyConcern",
  "interventionsAffected",
  "otherDisciplines",
];

const emptyValues = () => ({
  ...emptyRnMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};


const RnMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnMissedVisitPayload();
    const merged = { ...base, ...loadedPayload };

    ARRAY_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });

    const logs = Array.isArray(loadedPayload.attemptLogs)
      ? loadedPayload.attemptLogs
      : [];
    merged.attemptLogs = logs.map((row) => ({
      id: row.id || emptyAttemptLog().id,
      dateTime: row.dateTime || "",
      method: row.method || "Phone Call",
      outcome: row.outcome || "",
    }));
    merged.commonHeader = normalizeCommonHeaderState(loadedPayload.commonHeader);

    applyWoundAssessmentPayload(merged, merged, {
      mrn: patient?.mrn,
    });

    return merged;
  }, [patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        missedVisitDate: data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  /** Form-specific chart fields (demographics live in common header). */
  const buildPatientPrefill = useCallback(
    () => ({
      ...emptyRnMissedVisitPayload(),
      mrn: patient?.mrn || "",
    }),
    [patient],
  );

  const mergePatientSnapshot = useCallback(
    (existingPayload) => {
      const prefill = buildPatientPrefill();
      return {
        ...prefill,
        ...existingPayload,
        mrn: existingPayload.mrn || prefill.mrn || "",
      };
    },
    [buildPatientPrefill],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "Nurse Missed Visit",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getRnMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...mergePatientSnapshot(existingPayload), commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // Load once per form identity. Prefill helpers + patient chart are read
    // from the render that starts the request; including them in deps caused
    // an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateAttemptLog = (rowId, patch) => {
    setValues((prev) => ({
      ...prev,
      attemptLogs: prev.attemptLogs.map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addAttemptLog = () => {
    setValues((prev) => ({
      ...prev,
      attemptLogs: [...prev.attemptLogs, emptyAttemptLog()],
    }));
  };

  const removeAttemptLog = (rowId) => {
    setValues((prev) => ({
      ...prev,
      attemptLogs: prev.attemptLogs.filter((row) => row.id !== rowId),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      missedVisitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...rnFields
    } = values;

    const nestedPayload = withNormalizedCommonHeader({
      ...rnFields,
      dateOfBirth: values.dateOfBirth ? formatDateForInput(values.dateOfBirth) : "",
      missedVisitDate: missedVisitDate ? formatDateForInput(missedVisitDate) : "",
      attemptLogs: (values.attemptLogs || []).map((row) => ({
        id: row.id,
        dateTime: row.dateTime || "",
        method: row.method || "",
        outcome: row.outcome || "",
      })),
      commonHeader: values.commonHeader,
    });

    ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate ? formatDateForInput(missedVisitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnMissedVisitForm,
        filledByUserId,
        scheduleDate: values.missedVisitDate
          ? formatDateForInput(values.missedVisitDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Missed Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health missed visit documentation.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={MISSED_VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Reason for Missed Visit">
          <CheckboxGrid
            options={MISSED_REASON_OPTIONS}
            values={values.missedReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("missedReasons", opt)}
            columns={2}
          />
          <div className="mt-3">
            <Field label="Detailed Explanation">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.reasonExplanation}
                disabled={!canEdit}
                placeholder="Describe the circumstances leading to the missed visit…"
                onChange={(e) => setValue("reasonExplanation", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="8. Attempts Made to Complete Visit">
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr
                  className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-2 py-1.5 font-semibold w-10">#</th>
                  <th className="px-2 py-1.5 font-semibold w-44">Date / Time</th>
                  <th className="px-2 py-1.5 font-semibold w-36">Method</th>
                  <th className="px-2 py-1.5 font-semibold">Outcome</th>
                  {canEdit && (
                    <th className="px-2 py-1.5 font-semibold w-12 text-right">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {values.attemptLogs.length === 0 ? (
                  <tr className="border-t border-slate-100">
                    <td
                      colSpan={canEdit ? 5 : 4}
                      className="px-2 py-3 text-[11px] text-slate-400 text-center"
                    >
                      No attempt logs added
                    </td>
                  </tr>
                ) : (
                  values.attemptLogs.map((row, index) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-[12px] text-slate-500 tabular-nums">
                        {index + 1}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="datetime-local"
                          className={fieldClass}
                          value={row.dateTime}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateAttemptLog(row.id, { dateTime: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          className={fieldClass}
                          value={row.method}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateAttemptLog(row.id, { method: e.target.value })
                          }
                        >
                          {ATTEMPT_METHOD_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <textarea
                          rows={1}
                          className={textareaClass}
                          value={row.outcome}
                          disabled={!canEdit}
                          placeholder="Outcome of this attempt…"
                          onChange={(e) =>
                            updateAttemptLog(row.id, { outcome: e.target.value })
                          }
                        />
                      </td>
                      {canEdit && (
                        <td className="px-2 py-1 text-right">
                          <button
                            type="button"
                            onClick={() => removeAttemptLog(row.id)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                            aria-label="Remove attempt log"
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={addAttemptLog}
                className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
              >
                <PlusIcon size={13} />
                Add Log Entry
              </button>
            </div>
          )}
        </Section>

        <Section icon={HeartPulseIcon} title="9. Clinical Impact Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Risk of Hospitalization">
              <select
                className={fieldClass}
                value={values.riskHospitalization}
                disabled={!canEdit}
                onChange={(e) => setValue("riskHospitalization", e.target.value)}
              >
                <option value="">Select…</option>
                {RISK_LEVEL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Risk of Clinical Decline">
              <select
                className={fieldClass}
                value={values.riskClinicalDecline}
                disabled={!canEdit}
                onChange={(e) => setValue("riskClinicalDecline", e.target.value)}
              >
                <option value="">Select…</option>
                {RISK_LEVEL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Immediate Safety Concern?</SubHeading>
            <CheckboxGrid
              options={YES_NO_OPTIONS}
              values={values.safetyConcern}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("safetyConcern", opt)}
              columns={2}
            />
          </div>
          <div className="mt-3">
            <Field label="Clinical Impact Summary">
              <textarea
                rows={4}
                className={textareaClass}
                value={values.clinicalImpactSummary}
                disabled={!canEdit}
                placeholder="Summarize the clinical impact of this missed visit…"
                onChange={(e) => setValue("clinicalImpactSummary", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={NurseIcon} title="Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <Section icon={TargetIcon} title="10. Plan of Care Analysis">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <SubHeading>Interventions Affected</SubHeading>
              <CheckboxGrid
                options={INTERVENTION_OPTIONS}
                values={values.interventionsAffected}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("interventionsAffected", opt)}
                columns={1}
              />
            </div>
            <Field label="Functional Impact">
              <textarea
                rows={6}
                className={textareaClass}
                value={values.functionalImpact}
                disabled={!canEdit}
                placeholder="Describe the impact on the patient's functional status and plan of care…"
                onChange={(e) => setValue("functionalImpact", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="11. Coordination & Notifications">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Case Manager
              </div>
              <Field label="Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.caseManagerName}
                  disabled={!canEdit}
                  onChange={(e) => setValue("caseManagerName", e.target.value)}
                />
              </Field>
              <Field label="Method">
                <select
                  className={fieldClass}
                  value={values.caseManagerMethod}
                  disabled={!canEdit}
                  onChange={(e) => setValue("caseManagerMethod", e.target.value)}
                >
                  <option value="">Select…</option>
                  {NOTIFY_METHOD_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date">
                <DateFieldInput
                  name="caseManagerDate"
                  value={values.caseManagerDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("caseManagerDate", v)}
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Primary Physician
              </div>
              <Field label="Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.physicianName}
                  disabled={!canEdit}
                  onChange={(e) => setValue("physicianName", e.target.value)}
                />
              </Field>
              <Field label="Method">
                <select
                  className={fieldClass}
                  value={values.physicianMethod}
                  disabled={!canEdit}
                  onChange={(e) => setValue("physicianMethod", e.target.value)}
                >
                  <option value="">Select…</option>
                  {NOTIFY_METHOD_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date">
                <DateFieldInput
                  name="physicianDate"
                  value={values.physicianDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("physicianDate", v)}
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Family / Caregiver
              </div>
              <Field label="Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.caregiverName}
                  disabled={!canEdit}
                  onChange={(e) => setValue("caregiverName", e.target.value)}
                />
              </Field>
              <Field label="Method">
                <select
                  className={fieldClass}
                  value={values.caregiverMethod}
                  disabled={!canEdit}
                  onChange={(e) => setValue("caregiverMethod", e.target.value)}
                >
                  <option value="">Select…</option>
                  {NOTIFY_METHOD_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date">
                <DateFieldInput
                  name="caregiverDate"
                  value={values.caregiverDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("caregiverDate", v)}
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={CalendarIcon} title="12. Follow-Up Plan">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Rescheduled Visit Date">
              <DateFieldInput
                name="rescheduledDate"
                value={values.rescheduledDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("rescheduledDate", v)}
              />
            </Field>
            <Field label="Next Scheduled Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.nextScheduledFrequency}
                disabled={!canEdit}
                placeholder="e.g. 2x/week"
                onChange={(e) => setValue("nextScheduledFrequency", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Mitigation Strategy">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.mitigationStrategy}
                disabled={!canEdit}
                placeholder="Steps taken to prevent future missed visits…"
                onChange={(e) => setValue("mitigationStrategy", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Other Disciplines to Notify</SubHeading>
            <CheckboxGrid
              options={DISCIPLINE_OPTIONS}
              values={values.otherDisciplines}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("otherDisciplines", opt)}
              columns={3}
            />
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnMissedVisitForm;
